import type { AssignmentSuggestion, Page } from '@chukua/common';
import { Pagination } from 'antd';
import { SearchX, Truck } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { EligibilityState } from '../hooks/use-blocked-candidates';
import { groupRankedCrews } from '../policies/picker-candidates.policy';
import type { SuggestionPickerQuery } from '../policies/suggestion-picker-query';
import { BlockedSection } from './BlockedSection';
import { CrewPickerSections } from './CrewPickerSections';

interface SuggestionsPickerProps {
  page: Page<AssignmentSuggestion>;
  list: ListQueryControls<SuggestionPickerQuery>;
  eligibility: EligibilityState;
  isRefreshing: boolean;
  chosenRank: number | null;
  onChoose: (crew: AssignmentSuggestion) => void;
}

/** Search, the ranked crews in two sections, the server's paging, then the blocked units; the page size is the company's setting, so no size changer. */
export function SuggestionsPicker({ page, list, eligibility, isRefreshing, chosenRank, onChoose }: SuggestionsPickerProps) {
  const crews = groupRankedCrews(page.items);
  return (
    <div className={`flex flex-col gap-4 ${isRefreshing ? 'opacity-60' : ''}`} aria-busy={isRefreshing}>
      <ListSearch value={list.query.q} placeholder="Search truck, trailer or driver" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
      {page.items.length === 0 && (list.isFiltered ? <NoMatchingCrews /> : <NoEligibleCrew />)}
      {page.items.length > 0 && <CrewPickerSections crews={crews} chosenRank={chosenRank} onChoose={onChoose} />}
      {page.total > page.pageSize && (
        <Pagination
          current={page.page}
          pageSize={page.pageSize}
          total={page.total}
          showSizeChanger={false}
          onChange={(next) => list.setQuery({ page: next })}
        />
      )}
      <BlockedSection eligibility={eligibility} rankedOnPage={page.items} q={list.query.q} />
    </div>
  );
}

function NoMatchingCrews() {
  return <EmptyState icon={SearchX} title="No matching crews" description="No suggested crew has a truck, trailer or driver matching this search." />;
}

function NoEligibleCrew() {
  return (
    <EmptyState
      icon={Truck}
      title="No crew can be suggested"
      description="No available truck, trailer and driver pass the eligibility checks for this window. The blocked ones, and why, are below."
    />
  );
}
